import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import type { TransactionDetailDto } from '@shared/transactionDetailTypes';
import { generateTransactionReceipt } from '@shared/receiptGenerator';

interface LocalReceiptPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  detail: TransactionDetailDto | null;
  loading?: boolean;
  error?: string | null;
}

const LocalReceiptPreviewDialog: React.FC<LocalReceiptPreviewDialogProps> = ({
  open,
  onClose,
  detail,
  loading = false,
  error = null,
}) => {
  const receipt = useMemo(() => (detail ? generateTransactionReceipt(detail) : null), [detail]);

  const handleSave = () => {
    if (!receipt) return;
    const blob = new Blob([receipt.text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = receipt.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!receipt) return;
    const popup = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
    if (!popup) return;
    popup.document.write(`<pre style="font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; white-space: pre-wrap;">${receipt.text.replace(/</g, '&lt;')}</pre>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Receipt Preview</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && receipt && (
          <Box
            component="pre"
            sx={{
              mt: 1,
              mb: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '0.85rem',
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            {receipt.text}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handlePrint} disabled={!receipt || loading} variant="outlined">
          Print
        </Button>
        <Button onClick={handleSave} disabled={!receipt || loading} variant="contained">
          Save Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocalReceiptPreviewDialog;
